import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CAMPO, Carregando, Recado } from "./ui";
import { dataHora } from "../lib/admin";
import {
  ALTERNATIVAS,
  COLUNAS_DOS_CONCORRENTES,
  configurar,
  corrigir,
  exibir,
  frase,
  gravarConcorrente,
  lerCorrecao,
  novaEdicao,
  vincular,
  type Caderno,
  type ColunaDoConcorrente,
  type Leitura,
  type Posicao,
  type Questao,
  type Resposta,
  type Situacao,
} from "../lib/correcaoCfc";

// Admin › Correção CFC (000384): a correção ao vivo do Exame de Suficiência, que era o
// `admin-correcao-cfc` (Flutter, no Fly.io). As cinco seções de lá, uma por vez e no ENDEREÇO
// (`?secao=…&edicao=…`), para o link mandado a um colega abrir a mesma tela:
//
//   · Corrigir      — a alternativa, o enunciado e o comentário de cada questão de REFERÊNCIA;
//   · Cadernos      — qual questão de referência está em cada posição dos outros cadernos;
//   · Concorrentes  — o gabarito oficial e o dos concorrentes, com o CF ao lado;
//   · Quadro        — o que o quadro público mostra, a edição dele e o botão de baixar;
//   · Edições       — as edições (uma por exame) e a edição nova, e o registro.
//
// ZERO regra: o CF de cada posição e a situação de cada linha do comparativo vêm prontos do banco, e a
// recusa vem com a frase dele. Quem só VÊ recebe a tela sem os campos de escrita (é conforto, não
// trava: quem recusa é cada função `corr_admin_*`).

type Secao = "corrigir" | "cadernos" | "concorrentes" | "quadro" | "edicoes";
const SECOES: { id: Secao; rotulo: string }[] = [
  { id: "corrigir", rotulo: "Corrigir" },
  { id: "cadernos", rotulo: "Cadernos" },
  { id: "concorrentes", rotulo: "Concorrentes" },
  { id: "quadro", rotulo: "Quadro" },
  { id: "edicoes", rotulo: "Edições" },
];
type Recado_ = { tipo: "ok" | "erro"; texto: string } | null;
const BOTAO_SECUNDARIO =
  "min-h-10 rounded-full border border-borda px-4 text-sm font-semibold text-tinta transition hover:border-acento hover:text-titulo disabled:opacity-55";
const TH = "p-2 font-bold";

export default function AdminCorrecaoCfc() {
  const [busca, setBusca] = useSearchParams();
  const secao = (SECOES.find((s) => s.id === busca.get("secao"))?.id ?? "corrigir") as Secao;
  const edicaoPedida = busca.get("edicao");
  // A resposta guarda o PEDIDO a que responde, e a tela só a usa se ele for o de agora. Sem isso, ao
  // trocar de edição (ou logo depois de criar uma), a tela continuava mostrando a edição ANTERIOR, pronta
  // para clicar, até a leitura nova chegar — e "Corrigir" nessa janela corrigia a edição errada. Foi o
  // que a prova de tela fez em 03/11, na edição 2026.2 de verdade (a cicatriz no CLAUDE.md).
  const [estado, setEstado] = useState<{ pedido: string | null; r: Leitura | Resposta } | null>(null);
  const r = estado && estado.pedido === edicaoPedida ? estado.r : null;

  const reler = useCallback(async () => {
    const x = await lerCorrecao(edicaoPedida);
    setEstado({ pedido: edicaoPedida, r: x });
  }, [edicaoPedida]);
  useEffect(() => {
    let vivo = true;
    void lerCorrecao(edicaoPedida).then((x) => vivo && setEstado({ pedido: edicaoPedida, r: x }));
    return () => {
      vivo = false;
    };
  }, [edicaoPedida]);

  const ir = (mudar: Record<string, string | null>) => {
    const b = new URLSearchParams(busca);
    for (const [k, v] of Object.entries(mudar)) {
      if (v === null) b.delete(k);
      else b.set(k, v);
    }
    setBusca(b);
  };

  if (!r) return <Carregando texto="Lendo a Correção CFC…" />;
  if (!r.ok || !("edicoes" in r)) return <Recado tipo="erro" texto={frase(r as Resposta)} />;

  return (
    <div
      className="flex flex-col gap-5"
      data-correcao-cfc
      data-edicao-titulo={r.edicao?.titulo ?? ""}
      data-edicao-id={r.edicao?.id ?? ""}
      // O pedido a que esta tela responde: é por ele que a prova separa o defeito (a tela sabe do pedido
      // novo e mostra outra edição) do intervalo do roteador, que faz a troca de endereço como transição e
      // deixa a tela anterior de pé até o desenho seguinte.
      data-pedido={edicaoPedida ?? ""}
      data-pode-editar={r.pode_editar ? "" : undefined}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-titulo">Correção CFC</h2>
          <p className="mt-1 text-sm leading-relaxed text-cinza">
            A correção ao vivo do Exame de Suficiência: a equipe corrige o caderno de referência, e o quadro público mostra a
            resposta da casa nos quatro cadernos. Até a virada, o quadro de hoje continua no ar; a edição que a equipe mexer aqui
            deixa de ser atualizada pela carga.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {r.edicoes.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-cinza">
              Edição
              <select
                className={`${CAMPO} w-auto`}
                value={r.edicao?.id ?? ""}
                onChange={(e) => ir({ edicao: e.target.value })}
                data-escolher-edicao
              >
                {r.edicoes.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.titulo}
                    {e.atual ? " (no quadro)" : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
          <a href="/correcao-cfc" target="_blank" rel="noreferrer" className={BOTAO_SECUNDARIO + " inline-flex items-center"}>
            Abrir o quadro<span className="sr-only"> (abre em outra aba)</span>
          </a>
        </div>
      </div>

      <nav className="flex flex-wrap gap-2" aria-label="Seções da Correção CFC">
        {SECOES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => ir({ secao: s.id === "corrigir" ? null : s.id })}
            aria-current={secao === s.id ? "page" : undefined}
            className={`min-h-10 rounded-full px-4 text-sm font-semibold transition ${
              secao === s.id ? "bg-acento text-acento-tinta" : "border border-borda text-tinta hover:border-acento"
            }`}
            data-secao-correcao={s.id}
          >
            {s.rotulo}
          </button>
        ))}
      </nav>

      {!r.edicao && secao !== "edicoes" ? (
        <Recado tipo="erro" texto="Ainda não há edição nenhuma. Crie a primeira em Edições." />
      ) : secao === "corrigir" ? (
        <Corrigir key={r.edicao!.id} r={r} reler={reler} />
      ) : secao === "cadernos" ? (
        <Cadernos key={r.edicao!.id} r={r} reler={reler} />
      ) : secao === "concorrentes" ? (
        <Concorrentes key={r.edicao!.id} r={r} reler={reler} />
      ) : secao === "quadro" ? (
        <QuadroDaEdicao key={r.edicao!.id} r={r} reler={reler} />
      ) : (
        <Edicoes r={r} reler={reler} aoCriar={(id) => ir({ edicao: id, secao: "corrigir" })} />
      )}
    </div>
  );
}

type PropsSecao = { r: Leitura; reler: () => Promise<void> };

// ---------------------------------------------------------------------------
// Corrigir
// ---------------------------------------------------------------------------
function Corrigir({ r, reler }: PropsSecao) {
  const [filtro, setFiltro] = useState<"todas" | "respondidas" | "pendentes">("todas");
  const [aberta, setAberta] = useState<string | null>(null);
  const lista = r.questoes.filter((q) => (filtro === "todas" ? true : filtro === "respondidas" ? q.respondida : !q.respondida));
  const respondidas = r.questoes.filter((q) => q.respondida).length;
  return (
    <section className="surface p-4 sm:p-5" data-secao-corrigir>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-cinza">
          <strong className="text-titulo">{respondidas}</strong> de {r.questoes.length} questões respondidas (alternativa, enunciado,
          comentário e professor). O professor é quem salva a correção.
        </p>
        <div className="flex gap-1" role="group" aria-label="Filtrar as questões">
          {(["todas", "respondidas", "pendentes"] as const).map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filtro === f}
              onClick={() => setFiltro(f)}
              className={`min-h-9 rounded-full px-3 text-xs font-semibold ${filtro === f ? "bg-acento text-acento-tinta" : "border border-borda text-tinta"}`}
              data-filtro-questoes={f}
            >
              {f === "todas" ? "Todas" : f === "respondidas" ? "Respondidas" : "Pendentes"}
            </button>
          ))}
        </div>
      </div>
      <ul className="mt-4 divide-y divide-borda/70" data-lista-questoes>
        {lista.map((q) => (
          <li key={q.id} className="py-2" data-questao-ref={q.numero}>
            {aberta === q.id && r.pode_editar ? (
              <FormularioDaQuestao q={q} aoFechar={() => setAberta(null)} reler={reler} />
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <span className="w-10 font-bold text-titulo tabular-nums">{String(q.numero).padStart(2, "0")}</span>
                <span className="inline-flex min-w-12 justify-center rounded-md border border-borda px-2 py-0.5 font-bold text-tinta" data-alternativa-ref>
                  {q.alternativa ?? "—"}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-cinza" title={q.enunciado ?? undefined}>
                  {q.enunciado ?? "sem enunciado"}
                  {q.professor ? ` · ${q.professor}` : ""}
                </span>
                <span className={`text-xs font-semibold ${q.respondida ? "text-aviso-tinta" : "text-alerta-tinta"}`}>
                  {q.respondida ? "Respondida" : "Pendente"}
                </span>
                {r.pode_editar && (
                  <button type="button" className={BOTAO_SECUNDARIO} onClick={() => setAberta(q.id)} data-editar-questao={q.numero}>
                    Corrigir
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
        {lista.length === 0 && <li className="py-4 text-center text-sm text-cinza">Nenhuma questão neste filtro.</li>}
      </ul>
    </section>
  );
}

function FormularioDaQuestao({ q, aoFechar, reler }: { q: Questao; aoFechar: () => void; reler: () => Promise<void> }) {
  const [alt, setAlt] = useState(q.alternativa ?? "");
  const [enunciado, setEnunciado] = useState(q.enunciado ?? "");
  const [texto, setTexto] = useState(q.texto_alternativa ?? "");
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<Recado_>(null);
  const salvar = async (e: FormEvent) => {
    e.preventDefault();
    setSalvando(true);
    const x = await corrigir(q.id, alt || null, enunciado, texto);
    setSalvando(false);
    if (!x.ok) return setRecado({ tipo: "erro", texto: frase(x) });
    await reler();
    aoFechar();
  };
  return (
    <form onSubmit={salvar} className="flex flex-col gap-3" data-formulario-questao={q.numero}>
      <p className="font-bold text-titulo">Questão {String(q.numero).padStart(2, "0")}</p>
      <label className="flex flex-col gap-1 text-sm text-cinza">
        Alternativa
        <select className={`${CAMPO} max-w-48`} value={alt} onChange={(e) => setAlt(e.target.value)} data-campo-alternativa>
          <option value="">— sem resposta</option>
          {ALTERNATIVAS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-sm text-cinza">
        Enunciado
        <textarea className={CAMPO} rows={2} maxLength={2000} value={enunciado} onChange={(e) => setEnunciado(e.target.value)} data-campo-enunciado />
      </label>
      <label className="flex flex-col gap-1 text-sm text-cinza">
        Comentário da alternativa
        <textarea className={CAMPO} rows={2} maxLength={2000} value={texto} onChange={(e) => setTexto(e.target.value)} data-campo-comentario />
      </label>
      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
      <div className="flex gap-2">
        <button type="submit" className="pill-cta min-h-10 px-5 text-sm" disabled={salvando} data-salvar-questao>
          {salvando ? "Salvando…" : "Salvar a correção"}
        </button>
        <button type="button" className={BOTAO_SECUNDARIO} onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Cadernos (ligar as posições à referência)
// ---------------------------------------------------------------------------
function useCadernoEscolhido(cadernos: Caderno[], soOsOutros: boolean) {
  const opcoes = soOsOutros ? cadernos.filter((c) => !c.referencia) : cadernos;
  const [id, setId] = useState(opcoes[0]?.id ?? null);
  const caderno = opcoes.find((c) => c.id === id) ?? opcoes[0] ?? null;
  return { opcoes, caderno, escolher: setId };
}

function EscolherCaderno({ opcoes, caderno, escolher }: ReturnType<typeof useCadernoEscolhido>) {
  return (
    <div className="flex flex-wrap gap-1" role="group" aria-label="Caderno">
      {opcoes.map((c) => (
        <button
          key={c.id}
          type="button"
          aria-pressed={caderno?.id === c.id}
          onClick={() => escolher(c.id)}
          className={`min-h-9 rounded-full px-3 text-xs font-semibold ${caderno?.id === c.id ? "bg-acento text-acento-tinta" : "border border-borda text-tinta"}`}
          data-escolher-caderno={c.ordem}
        >
          {c.titulo}
          {c.referencia ? " (referência)" : ""}
        </button>
      ))}
    </div>
  );
}

function Cadernos({ r, reler }: PropsSecao) {
  const escolha = useCadernoEscolhido(r.cadernos, true);
  const [recados, setRecados] = useState<Record<number, Recado_>>({});
  const c = escolha.caderno;
  const ligar = async (p: Posicao, questaoId: string) => {
    if (!c) return;
    const x = await vincular(c.id, p.numero, questaoId || null);
    setRecados((m) => ({ ...m, [p.numero]: x.ok ? null : { tipo: "erro", texto: frase(x) } }));
    if (x.ok) await reler();
  };
  return (
    <section className="surface p-4 sm:p-5" data-secao-cadernos>
      <p className="text-sm text-cinza">
        Cada caderno tem as mesmas questões em outra ordem. Diga qual questão do caderno de referência está em cada posição: a
        correção chega sozinha a todos os cadernos ligados.
      </p>
      <div className="mt-3">
        <EscolherCaderno {...escolha} />
      </div>
      {!c ? (
        <p className="mt-4 text-sm text-cinza">Esta edição só tem o caderno de referência.</p>
      ) : (
        <ol className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-posicoes={c.ordem}>
          {c.posicoes.map((p) => (
            <li key={p.numero} className="flex flex-col gap-1" data-posicao={p.numero}>
              <label className="flex items-center gap-2 text-sm text-tinta">
                <span className="w-24 shrink-0 font-semibold tabular-nums">
                  {c.titulo}, {String(p.numero).padStart(2, "0")}
                </span>
                <select
                  className={`${CAMPO} w-auto`}
                  value={p.questao_id ?? ""}
                  disabled={!r.pode_editar}
                  onChange={(e) => void ligar(p, e.target.value)}
                  data-ligar-posicao={p.numero}
                >
                  <option value="">— não ligada</option>
                  {r.questoes.map((q) => (
                    <option key={q.id} value={q.id}>
                      Referência {String(q.numero).padStart(2, "0")}
                    </option>
                  ))}
                </select>
              </label>
              {recados[p.numero] && <p className="text-xs text-erro-tinta" role="alert">{recados[p.numero]!.texto}</p>}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Concorrentes
// ---------------------------------------------------------------------------
const ROTULO_DA_SITUACAO: Record<Situacao, string> = {
  confere: "confere",
  diverge: "diverge",
  incompleta: "falta preencher",
  sem_cf: "sem correção",
};

function Concorrentes({ r, reler }: PropsSecao) {
  const escolha = useCadernoEscolhido(r.cadernos, false);
  const c = escolha.caderno;
  const [recado, setRecado] = useState<Recado_>(null);
  const contas = useMemo(() => {
    const n: Record<Situacao, number> = { confere: 0, diverge: 0, incompleta: 0, sem_cf: 0 };
    for (const p of c?.posicoes ?? []) n[p.situacao]++;
    return n;
  }, [c]);
  const gravar = async (p: Posicao, coluna: ColunaDoConcorrente, valor: string) => {
    if (!c || (p[coluna] ?? "") === valor.replace(/\s/g, "").toUpperCase()) return;
    const x = await gravarConcorrente(c.id, p.numero, coluna, valor);
    setRecado(x.ok ? null : { tipo: "erro", texto: frase(x) });
    await reler();
  };
  return (
    <section className="surface p-4 sm:p-5" data-secao-concorrentes>
      <p className="text-sm text-cinza">
        O gabarito oficial e o dos concorrentes, posição a posição. O CF é a correção da casa, que vem da questão de referência
        ligada.
      </p>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <EscolherCaderno {...escolha} />
        <p className="text-xs text-cinza" data-contas-concorrentes>
          {contas.confere} conferem · {contas.diverge} divergem · {contas.incompleta} por preencher · {contas.sem_cf} sem correção
        </p>
      </div>
      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
      {c && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-180 text-sm" data-tabela-concorrentes={c.ordem}>
            <thead>
              <tr className="border-b border-borda text-left text-xs text-cinza uppercase">
                <th className={TH}>Nº</th>
                <th className={TH}>CF</th>
                {COLUNAS_DOS_CONCORRENTES.map((k) => (
                  <th key={k.id} className={TH}>
                    {k.rotulo}
                  </th>
                ))}
                <th className={TH}>Situação</th>
              </tr>
            </thead>
            <tbody>
              {c.posicoes.map((p) => (
                <tr
                  key={p.numero}
                  className={`border-b border-borda/60 ${p.situacao === "diverge" ? "bg-erro-fundo" : p.situacao === "confere" ? "bg-aviso-fundo" : ""}`}
                  data-linha-concorrente={p.numero}
                  data-situacao={p.situacao}
                >
                  <td className="p-2 font-bold text-titulo tabular-nums">{String(p.numero).padStart(2, "0")}</td>
                  <td className="p-2 font-bold text-tinta">{p.cf ?? "—"}</td>
                  {COLUNAS_DOS_CONCORRENTES.map((k) => (
                    <td key={k.id} className="p-1">
                      <input
                        className={`${CAMPO} w-20 py-1 text-center uppercase`}
                        defaultValue={p[k.id] ?? ""}
                        key={`${k.id}-${p[k.id] ?? ""}`}
                        maxLength={10}
                        disabled={!r.pode_editar}
                        aria-label={`${k.rotulo}, questão ${p.numero}`}
                        onBlur={(e) => void gravar(p, k.id, e.target.value)}
                        data-celula-concorrente={`${p.numero}-${k.id}`}
                      />
                    </td>
                  ))}
                  <td className={`p-2 text-xs font-semibold ${p.situacao === "diverge" ? "text-erro-tinta" : p.situacao === "confere" ? "text-aviso-tinta" : "text-cinza"}`}>
                    {ROTULO_DA_SITUACAO[p.situacao]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Quadro (o que o quadro público mostra)
// ---------------------------------------------------------------------------
function QuadroDaEdicao({ r, reler }: PropsSecao) {
  const e = r.edicao!;
  const [recado, setRecado] = useState<Recado_>(null);
  const [ocupado, setOcupado] = useState(false);
  const fazer = async (acao: () => Promise<Resposta & { mudadas?: number }>, ok: (x: Resposta & { mudadas?: number }) => string) => {
    setOcupado(true);
    const x = await acao();
    setOcupado(false);
    setRecado(x.ok ? { tipo: "ok", texto: ok(x) } : { tipo: "erro", texto: frase(x) });
    if (x.ok) await reler();
  };
  const visiveis = r.questoes.filter((q) => q.visivel).length;
  return (
    <section className="surface flex flex-col gap-4 p-4 sm:p-5" data-secao-quadro>
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-tinta" data-edicao-no-quadro={e.atual ? "" : undefined}>
          {e.atual
            ? `A edição ${e.titulo} é a que o quadro público mostra.`
            : `O quadro público não mostra a edição ${e.titulo}.`}
        </p>
        {r.pode_editar && !e.atual && (
          <button
            type="button"
            className="pill-cta min-h-10 px-4 text-sm"
            disabled={ocupado}
            onClick={() => void fazer(() => configurar(e.id, true, null), () => `A edição ${e.titulo} passou a ser a do quadro.`)}
            data-marcar-atual
          >
            Pôr esta edição no quadro
          </button>
        )}
      </div>
      <p className="text-sm text-cinza" data-visiveis>
        <strong className="text-titulo">{visiveis}</strong> de {r.questoes.length} questões aparecem no quadro. O que está
        escondido não sai do banco: ninguém vê a resposta antes de a equipe liberar.
      </p>
      {r.pode_editar && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={BOTAO_SECUNDARIO}
            disabled={ocupado}
            onClick={() => void fazer(() => exibir(e.id, null, true), (x) => `${x.mudadas ?? 0} questões passaram a aparecer.`)}
            data-mostrar-todas
          >
            Mostrar todas
          </button>
          <button
            type="button"
            className={BOTAO_SECUNDARIO}
            disabled={ocupado}
            onClick={() => void fazer(() => exibir(e.id, null, false), (x) => `${x.mudadas ?? 0} questões saíram do quadro.`)}
            data-esconder-todas
          >
            Esconder todas
          </button>
          <label className="ml-auto flex items-center gap-2 text-sm text-tinta">
            <input
              type="checkbox"
              checked={e.mostrar_download}
              disabled={ocupado}
              onChange={(ev) =>
                void fazer(
                  () => configurar(e.id, null, ev.target.checked),
                  () => (ev.target.checked ? "O quadro passou a ter os botões de baixar." : "O quadro saiu sem os botões de baixar."),
                )
              }
              data-mostrar-download
            />
            Botões de baixar as imagens no quadro
          </label>
        </div>
      )}
      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
      <ul className="grid grid-cols-5 gap-2 sm:grid-cols-10" data-lista-exibicao>
        {r.questoes.map((q) => (
          <li key={q.id}>
            <label
              className={`flex min-h-11 cursor-pointer items-center justify-center gap-1.5 rounded-lg border text-sm font-semibold ${
                q.visivel ? "border-acento bg-chip text-titulo" : "border-borda text-cinza"
              }`}
            >
              <input
                type="checkbox"
                checked={q.visivel}
                disabled={!r.pode_editar || ocupado}
                onChange={(ev) => void fazer(() => exibir(e.id, q.id, ev.target.checked), () => `Questão ${q.numero} ${ev.target.checked ? "no quadro" : "fora do quadro"}.`)}
                aria-label={`Questão ${q.numero} no quadro`}
                data-exibir-questao={q.numero}
              />
              {String(q.numero).padStart(2, "0")}
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edições
// ---------------------------------------------------------------------------
function Edicoes({ r, reler, aoCriar }: PropsSecao & { aoCriar: (id: string) => void }) {
  const [titulo, setTitulo] = useState("");
  const [data, setData] = useState("");
  const [cadernos, setCadernos] = useState(4);
  const [questoes, setQuestoes] = useState(50);
  const [motivo, setMotivo] = useState("");
  const [recado, setRecado] = useState<Recado_>(null);
  const [salvando, setSalvando] = useState(false);
  const criar = async (e: FormEvent) => {
    e.preventDefault();
    setSalvando(true);
    const x = await novaEdicao(titulo.trim(), data, cadernos, questoes, motivo);
    setSalvando(false);
    const id = (x as Resposta & { id?: string }).id;
    if (!x.ok || !id) return setRecado({ tipo: "erro", texto: frase(x) });
    setRecado({ tipo: "ok", texto: frase(x) });
    await reler();
    aoCriar(id);
  };
  return (
    <div className="flex flex-col gap-5">
      <section className="surface p-4 sm:p-5" data-secao-edicoes>
        <h3 className="font-bold text-titulo">As edições</h3>
        <p className="mt-1 text-sm text-cinza">
          Uma edição por exame. A edição nova não apaga a anterior: ela fica de histórico, e o quadro mostra a que estiver marcada.
        </p>
        <ul className="mt-3 divide-y divide-borda/70" data-lista-edicoes>
          {r.edicoes.map((e) => (
            <li key={e.id} className="flex flex-wrap items-center gap-3 py-2" data-edicao={e.titulo}>
              <Link to={`?edicao=${e.id}`} className="font-semibold text-link hover:underline">
                {e.titulo}
              </Link>
              <span className="text-sm text-cinza">{e.data_prova ? `prova em ${e.data_prova.split("-").reverse().join("/")}` : "sem data"}</span>
              {e.atual && <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-titulo">no quadro</span>}
            </li>
          ))}
          {r.edicoes.length === 0 && <li className="py-3 text-sm text-cinza">Nenhuma edição ainda.</li>}
        </ul>
      </section>

      {r.pode_editar && (
        <form onSubmit={criar} className="surface flex flex-col gap-3 p-4 sm:p-5" data-nova-edicao>
          <h3 className="font-bold text-titulo">Edição nova</h3>
          <p className="text-sm text-cinza">
            Nasce com os cadernos (o Tipo 1 é a referência), as questões em branco e o comparativo vazio, e fora do quadro: ponha-a
            no quadro quando for a hora.
          </p>
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-col gap-1 text-sm text-cinza">
              Exame (ano e semestre)
              <input className={`${CAMPO} w-32`} placeholder="2027.1" value={titulo} onChange={(e) => setTitulo(e.target.value)} data-campo-titulo />
            </label>
            <label className="flex flex-col gap-1 text-sm text-cinza">
              Data da prova
              <input type="date" className={`${CAMPO} w-44`} value={data} onChange={(e) => setData(e.target.value)} data-campo-data />
            </label>
            <label className="flex flex-col gap-1 text-sm text-cinza">
              Cadernos
              <input type="number" min={1} max={8} className={`${CAMPO} w-24`} value={cadernos} onChange={(e) => setCadernos(Number(e.target.value))} />
            </label>
            <label className="flex flex-col gap-1 text-sm text-cinza">
              Questões
              <input type="number" min={1} max={200} className={`${CAMPO} w-24`} value={questoes} onChange={(e) => setQuestoes(Number(e.target.value))} />
            </label>
          </div>
          <label className="flex flex-col gap-1 text-sm text-cinza">
            Por que criar
            <input className={CAMPO} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="o 1º Exame de 2027" data-campo-motivo />
          </label>
          {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
          <div>
            <button type="submit" className="pill-cta min-h-11 px-5 text-sm" disabled={salvando} data-criar-edicao>
              {salvando ? "Criando…" : "Criar a edição"}
            </button>
          </div>
        </form>
      )}

      <section className="surface p-4 sm:p-5" data-registro>
        <h3 className="font-bold text-titulo">Registro{r.edicao ? ` da edição ${r.edicao.titulo}` : ""}</h3>
        <ul className="mt-2 divide-y divide-borda/70 text-sm">
          {r.registro.map((m, i) => (
            <li key={i} className="py-1.5 text-tinta">
              <span className="text-cinza tabular-nums">{dataHora(m.em)}</span> · {m.por ?? "—"} {m.ato}
              {m.alvo ? `: ${m.alvo}` : ""}
              {m.motivo ? ` — ${m.motivo}` : ""}
            </li>
          ))}
          {r.registro.length === 0 && <li className="py-2 text-cinza">Nada registrado pela tela do hub ainda.</li>}
        </ul>
      </section>
    </div>
  );
}
